import { useEffect, useMemo, useState } from "react"
import { Discussion as DiscussionI } from "@/models/Discussions"
import { DiscussionWithTop, useDiscussions } from "@/services/store/useDiscussions"
import { ExpandedDiscussion } from "./components/ExpandedDiscussion"
import { calculateAdjustedTopPosition } from "@/components/designSystem/Discussions/utils"
import { CombinedDiscussionsChips } from "@/components/designSystem/Discussions/components/DiscussionChip/CombinedDiscussionsChips"
import { motion } from "framer-motion"

interface Props {
  current: DiscussionI
  discussionNumber: number
  crId: string
  commitSha: string
  total: number
  topPosition: number
}

export const Discussion = ({ current, discussionNumber, crId, commitSha, topPosition }: Props) => {
  const [adjustedTopPosition, setAdjustedTopPosition] = useState(0)
  const [discussionsForThisPosition, setDiscussionsForThisPosition] = useState<DiscussionWithTop[]>([])
  const [hasMultipleDiscussions, setHasMultipleDiscussions] = useState(false)
  const [selectedDiscussionId, setSelectedDiscussionId] = useState(current.id)

  const { discussionsExpanded, groupedDiscussions } = useDiscussions()

  const selectedDiscussion = useMemo(() => {
    if (discussionsForThisPosition.length === 1) return current
    return discussionsForThisPosition.find((d) => d.id === selectedDiscussionId) || current
  }, [current, discussionsForThisPosition, selectedDiscussionId])

  const isResolved = useMemo(() => {
    return selectedDiscussion.discussion_state === "resolved"
  }, [selectedDiscussion])

  useEffect(() => {
    const updatePositions = () => {
      setAdjustedTopPosition(calculateAdjustedTopPosition(topPosition))

      if (groupedDiscussions[topPosition]) {
        const discussions = groupedDiscussions[topPosition]
        setDiscussionsForThisPosition(discussions)
        setHasMultipleDiscussions(discussions.length > 1)

        // If we have discussions and the current selected ID isn't in this group
        // Then set the selected ID to the first discussion in the group
        if (discussions.length > 0 && !discussions.some((d) => d.id === selectedDiscussionId)) {
          setSelectedDiscussionId(discussions[0].id)
        }
      }
    }

    updatePositions()
    const timerId = setTimeout(updatePositions, 300)

    return () => clearTimeout(timerId)
  }, [groupedDiscussions, topPosition, selectedDiscussionId])

  const expandedVariants = {
    initial: { y: 5 },
    animate: { y: 0, transition: { delay: 0.1 } },
    hover: { y: -4, transition: { duration: 0.15 } }
  }

  return (
    <div
      id={current.id}
      data-discussion-number={discussionNumber}
      style={{ top: adjustedTopPosition }}
      className={`absolute right-0 z-10 flex w-full hover:z-20 ${!discussionsExpanded ? "justify-center" : ""}`}
    >
      {discussionsExpanded ? (
        <div className="w-full">
          {hasMultipleDiscussions && (
            <div className="bg-background/65 mx-2 p-2">
              <CombinedDiscussionsChips
                isExpanded={true}
                showSelectedChip={true}
                discussions={discussionsForThisPosition}
                selectedDiscussionId={selectedDiscussionId}
                setSelectedDiscussionId={setSelectedDiscussionId}
              />
            </div>
          )}

          <motion.div
            className="flex w-full justify-center"
            id="expanded-discussion-wrapper"
            variants={expandedVariants}
            initial="initial"
            animate="animate"
            whileHover="hover"
          >
            <ExpandedDiscussion
              selectedDiscussion={selectedDiscussion}
              discussionNumber={discussionNumber}
              isResolved={isResolved}
              crId={crId}
              commitSha={commitSha}
            />
          </motion.div>
        </div>
      ) : (
        <div className="flex justify-center">
          <CombinedDiscussionsChips
            isExpanded={false}
            showSelectedChip={false}
            discussions={discussionsForThisPosition}
            selectedDiscussionId={selectedDiscussionId}
            setSelectedDiscussionId={setSelectedDiscussionId}
          />
        </div>
      )}
    </div>
  )
}
